// src/components/navigation/navbar.tsx
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "@/components/logo";
import { dataNav } from "@/config/menu";
import { ThemeToggle } from "@/components/theme-toggle";
import { HelpCircle, Settings, Loader2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { useCampaignData } from "@/contexts/campaign-data";
import { useAccounts } from "@/contexts/account-context";
import { useUser } from '@clerk/clerk-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const DEBUG = process.env.NODE_ENV === 'development';

interface NavbarProps {
  // No props needed now
}

export function Navbar({ }: NavbarProps) {
  const location = useLocation();
  const { accounts, activeDexieId, switchAccount } = useAccounts();
  const isLoading = useCampaignData().loadingStatus !== 'idle';

  const handleAccountSwitch = async (newDexieId: string) => {
    try {
      console.log('Navbar account switch:', {
        current: activeDexieId,
        new: newDexieId
      })
      await switchAccount(newDexieId)
    } catch (err) {
      console.error('Account switch failed:', err)
    }
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto max-w-8xl px-4 sm:px-6 lg:px-8 flex h-14 items-center">
        <div className="flex items-center space-x-4">
          {/* Logo */}
          <Link
            to="/"
            className="mr-4 flex items-center space-x-2 transition-all duration-200 hover:opacity-80"
          >
            <Logo />
            <span className="hidden font-bold sm:inline-block">
              8020agent
            </span>
          </Link>

          {/* Account Selector */}
          <Select
            value={activeDexieId}
            onValueChange={handleAccountSwitch}
            disabled={isLoading}
          >
            <SelectTrigger className="w-[200px]">
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Switching...</span>
                </div>
              ) : (
                <SelectValue placeholder="Select account" />
              )}
            </SelectTrigger>
            <SelectContent>
              {accounts.map((account) => (
                <SelectItem
                  key={account.dexieId}
                  value={account.dexieId}
                  className="flex items-center gap-2"
                >
                  {account.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Navigation items using NavigationMenu for list layout */}
          {/* and DropdownMenu for dropdowns */}
          <NavigationMenu>
            <NavigationMenuList>
              {dataNav.map((item) => {
                if (item.devOnly && !DEBUG) {
                  return null;
                }

                // Use DropdownMenu for items with children
                if (item.items) {
                  return (
                    <NavigationMenuItem key={item.title}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          {/* Mimic NavigationMenuTrigger styling */}
                          <Button variant="ghost" className="h-9 px-4 py-2 flex items-center gap-1">
                            {item.title}
                            <ChevronDown className="relative top-[1px] ml-1 h-3 w-3 transition duration-300 group-data-[state=open]:rotate-180" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          {item.items.map((subItem) => (
                            <DropdownMenuItem key={subItem.title} asChild>
                              <Link
                                to={subItem.href || '#'}
                                className={cn(
                                  "w-full block", // Ensure link takes full width
                                  location.pathname === subItem.href && "bg-accent"
                                )}
                              >
                                {subItem.title}
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </NavigationMenuItem>
                  );
                }

                // Render Standard Link Item using NavigationMenuLink
                return (
                  <NavigationMenuItem key={item.title}>
                    <NavigationMenuLink asChild>
                      <Link
                        to={item.href || '#'}
                        className={cn(
                          navigationMenuTriggerStyle(),
                          location.pathname === item.href ? "bg-accent text-accent-foreground" : "",
                          item.title === 'Membership' ? 'font-bold text-primary' : ''
                        )}
                      >
                        {item.title}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                );
              })}
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        <div className="flex items-center space-x-4 ml-auto">
          <ThemeToggle />

          {/* Settings icon */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/settings"
                  className={cn(
                    "flex items-center justify-center h-8 w-8 rounded-md transition-all duration-200",
                    "hover:translate-y-[-1px] active:translate-y-[1px]",
                    location.pathname === '/settings'
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent/50 text-foreground/60"
                  )}
                >
                  <Settings className="h-5 w-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Settings</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Help & Feedback icon */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/help"
                  className={cn(
                    "flex items-center justify-center h-8 w-8 rounded-md transition-all duration-200",
                    "hover:translate-y-[-1px] active:translate-y-[1px]",
                    location.pathname === '/help'
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent/50 text-foreground/60"
                  )}
                >
                  <HelpCircle className="h-5 w-5" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Help & Feedback</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Auth buttons */}
          <SignedOut>
            <Button variant="outline" asChild>
              <Link to="/sign-in">Sign In</Link>
            </Button>
          </SignedOut>
          <div className="relative z-9999">
            <SignedIn>
              <UserButton
                appearance={{
                  elements: {
                    avatarBox: "w-8 h-8"
                  }
                }}
              />
            </SignedIn>
          </div>
        </div>
      </div>
    </header>
  );
}